//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular from 'angular';
import { INgRedux } from 'ng-redux';
import { BoundActionCreatorMap } from 'redux-extensions';

import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import { getRightsRequestStatuses } from '@apis/gatewayMediaApi';
import { muteSelectedItems } from '@apis/video-processing/videoProcessingApi';
import { fullScreenWorkflowActions } from '@galleries/actions/fullScreenWorkflowActions';
import { galleriesAddToGalleriesModalActions } from '@galleries/actions/galleriesAddToGalleriesModalActions';
import { noGalleriesModalActions } from '@galleries/actions/noGalleriesModalActions';
import { requestRightsModalActions } from '@galleries/actions/requestRightsModalActions';
import { rightsOwnedOrPendingModalActions } from '@galleries/actions/rightsOwnedOrPendingModalActions';
import { FULLSCREEN_WORKFLOW_FLAGS } from '@galleries/models/fullScreenWorkflowModel';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { rightsOwnedOrPendingCombiner } from '@galleries/selectors/bulk-rights-selectors/getSplitRightsOwnedOrPendingMedia';
import { getAllInstaShopGalleries } from '@galleries/selectors/getAllInstaShopGalleries';
import getGalleriesClientType, {
  GalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import { instagramAccountActions } from '@instagram-account/redux/account/instagramAccountActions';
import * as like2BuyModerationActions from '@instagram-account/redux/like2buy/like2buyModerationSlice';
import * as like2buyModerationModalActions from '@instagram-account/redux/like2buy/modals/like2buyModalsSlice';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { requestRightsActions } from '@legacy-components/rights-management/request-rights-actions';
import { ICuralateStore } from '@models/AppState';
import { IRightsRequestState } from '@models/IItemMetadata';
import { IBulkActionsActions } from 'app/bulk-actions/bulk-actions-actions/bulk-actions-actions';
import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import template from 'app/bulk-actions/bulk-actions-container/bulk-actions-container.html';
import { crl8 } from 'app/curalate.globals';

import 'app/bulk-actions/bulk-actions-actions/bulk-actions-actions';
import 'app/bulk-actions/reducers/bulk-actions-reducer';
import 'app/bulk-actions/uia-bulk-actions/uia-bulk-actions';
import { getClientId } from '@redux/selectors/getClientId';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';
import { FeatureNames } from '@bvengineering/curalate-features';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { getItemsMetadata } from '@apis/bulkActionsApi';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { imagesSelectionFailedToast } from 'app/bulk-actions/bulk-actions-container/bulk-actions-toasts';
import { UniversalItemType } from '@models/UniversalItemType';
import {
  getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  getIsBazaarvoiceNetworkSyndicationOnlyFullClientEnabled,
} from '@uia/rights/selectors/getUIARights';
import { getIsUiaVideoClosedCaptionsEnabled } from '@legacy-components/common/universal-image-actions/selectors/getIsUiaVideoClosedCaptionsEnabled';
const externalActions = {
  openRequestRightsModal: requestRightsModalActions.openRequestRightsModal,
  openRightsOwnedOrPendingModal: rightsOwnedOrPendingModalActions.openModal,
  openAddToGalleriesModal: galleriesAddToGalleriesModalActions.openModal,
  openNoGalleriesModal: noGalleriesModalActions.openModal,
  addConditionalFinalToast: fullScreenWorkflowActions.addConditionalFinalToast,
  fetchGalleries: galleriesDataActions.getGalleries,
  addToInstaShopGallery: galleriesDataActions.addToInstaShopGalleries,
  fetchDefaultInstagramAccount:
    instagramAccountActions.fetchDefaultInstagramAccount,
  requestRightsConfigurations: requestRightsActions.requestRightsConfigurations,
  addToastNotification: toastNotificationsActions.addToastNotification,
  openHideFromLike2BuyModal:
    like2buyModerationModalActions.openHideFromLike2BuyModal,
  openDeleteFromLike2BuyModal:
    like2buyModerationModalActions.openDeleteFromLike2BuyModal,
  pinLike2BuyItems: like2BuyModerationActions.pinLike2BuyItems,
};

export interface BulkActionsContainer
  extends ReturnType<(typeof BulkActionsContainer)['mapStateToThis']>,
    BoundActionCreatorMap<IBulkActionsActions>,
    BoundActionCreatorMap<typeof externalActions> {}

export class BulkActionsContainer {
  private static mapStateToThis(state: ICuralateStore) {
    const isAllInstagramItems = state.bulkActions.selectedItems.every(
      (item) => {
        const { itemType } = Cuid.parse(
          Cuid.fromPartial(item.cuid, getClientId(state)),
        );
        return (
          itemType === UniversalItemType.Facebook ||
          itemType === UniversalItemType.Instagram
        );
      },
    );
    return {
      confirmationModal: state.bulkActions.confirmationModal,
      currentCollection: state.bulkActions.currentCollection,
      disabledActions: state.bulkActions.disabledActions,
      isCollectionsEnabled: state.bulkActions.isCollectionsEnabled,
      isInstaShopEnabled: isAllInstagramItems,
      isLabelsEnabled: state.bulkActions.isLabelsEnabled,
      isLibraryEnabled: state.bulkActions.isLibraryEnabled,
      isShareV2Vendor: state.session.client.isShareV2Vendor,
      selectedItems: state.bulkActions.selectedItems,
      isVideoActionInProgress: state.bulkActions.isVideoActionInProgress,
      selectedGalleryId: state.galleries.picker.selectedGalleryId,
      isGalleriesEnabled: state.session.features.galleries,
      isInstaShopClient:
        getGalleriesClientType(state) === GalleriesClientType.instashop,
      defaultInstagramAccount: state.instagram.account.defaultInstagramAccount,
      areRightsConfigurationsFetched:
        state.requestRights.areConfigurationsFetched,
      isRightsRequestsNonAtMentionedModalOpen:
        state.galleries.requestRightsNonMentionedModal.isOpen,
      isRightRequestAtMentionedModalOpen:
        state.galleries.requestRightsModal.isOpen,
      selectedRightsStatuses: state.bulkActions.rightsRequestStatuses,
      selectedItemsForCaptions: state.bulkActions.selectedItemsForCaptions,
      allInstaShopGalleries: getAllInstaShopGalleries(state),
      isBazaarvoiceSyndicationEnabled:
        getIsBazaarvoiceNetworkSyndicationFrontendEnabled(state),
      isVideoCaptionsFeatureEnabled: getIsUiaVideoClosedCaptionsEnabled(state),
      isBazaarvoiceNetworkSyndicationOnlyFullClient:
        getIsBazaarvoiceNetworkSyndicationOnlyFullClientEnabled(state),
      clientId: getClientId(state),
    };
  }

  public isLoadingRequestRights: boolean;
  public isAddButtonLoading: boolean;

  private unsubscribe: () => void;

  public constructor(
    private $ngRedux: INgRedux,
    BulkActionsActions: IBulkActionsActions,
  ) {
    this.isLoadingRequestRights = false;

    const actions = {
      ...BulkActionsActions,
      ...externalActions,
    };

    this.unsubscribe = $ngRedux.connect(
      BulkActionsContainer.mapStateToThis,
      actions,
    )(this);
  }

  public $onInit() {
    this.loadGlobalData();
  }

  public $onDestroy() {
    this.unsubscribe();
  }

  public async onRequestRights() {
    this.isLoadingRequestRights = true;

    const partialCuids = this.selectedItems.map(
      (selectedItem) => selectedItem.cuid,
    );

    const contentLibraryItemsPromise = getContentLibraryItems({
      itemIds: partialCuids,
    }).then(
      (contentLibraryItemsResponse) => contentLibraryItemsResponse.contentItems,
    );

    const rightsMapPromise = getRightsRequestStatuses(partialCuids);

    const instagramAccountPromise = this.defaultInstagramAccount
      ? this.fetchDefaultInstagramAccount()
      : Promise.resolve(undefined);

    const requestMessagesPromise = this.areRightsConfigurationsFetched
      ? Promise.resolve(undefined)
      : this.requestRightsConfigurations();

    const [contentItems, { data: rightsStatuses }] = await Promise.all([
      contentLibraryItemsPromise,
      rightsMapPromise,
      instagramAccountPromise,
      requestMessagesPromise,
    ]);

    const { rightsNotOwnedItems, rightsOwnedOrPendingItems } =
      rightsOwnedOrPendingCombiner(contentItems, rightsStatuses);

    if (rightsNotOwnedItems.length) {
      this.addConditionalFinalToast(
        {
          title: 'Success',
          message: 'Your items have Rights Requested',
        },
        [FULLSCREEN_WORKFLOW_FLAGS.RIGHTS_REQUESTED],
      );
      this.openRequestRightsModal(rightsNotOwnedItems);
    }

    if (rightsOwnedOrPendingItems.length) {
      this.openRightsOwnedOrPendingModal(
        rightsOwnedOrPendingItems,
        contentItems.length,
      );
    }

    this.isLoadingRequestRights = false;
  }

  public async onClickAddToGalleries() {
    this.isAddButtonLoading = true;
    const galleries = await this.fetchGalleries();
    this.isAddButtonLoading = false;

    if (
      galleries.length === 0 &&
      !this.isBazaarvoiceNetworkSyndicationOnlyFullClient
    ) {
      this.openNoGalleriesModal();
    } else {
      await this.openAddToGalleriesModal(this.selectedItems);
    }
  }

  public async onClickAddToInstaShop() {
    if (!this.isInstaShopClient) {
      crl8.logger?.cloud.error(
        'Non InstaShop client clicked "Add to InstaShop"',
      );
      return;
    }

    this.isAddButtonLoading = true;
    await this.fetchGalleries();
    const partialCuids = this.selectedItems.map(
      (selectedItem) => selectedItem.cuid,
    );
    const partialCuidsWithoutRights = partialCuids.filter(
      (partialCuid) => !this.selectedRightsStatuses[partialCuid],
    );

    if (partialCuidsWithoutRights.length > 0) {
      await this.requestRightsStatuses();
    }

    const allRightsStatuses = partialCuids
      .map(
        (partialCuid) =>
          this.selectedRightsStatuses[partialCuid] as IRightsRequestState,
      )
      .map((rightsRequestState) => rightsRequestState.status);

    if (
      allRightsStatuses.find(
        (rightsStatus) => rightsStatus !== RIGHTS_STATUS.BRAND_OWNED,
      )
    ) {
      this.addToastNotification({
        title: "Some Content Isn't Brand-Owned",
        type: ToastType.ERROR,
        message: `Cannot add selected items to InstaShop.
          All selected items must be posted by your brand\'s
          Instagram account.`,
      });
      this.clearSelectedItems();
    } else if (this.allInstaShopGalleries.length === 0) {
      this.openNoGalleriesModal();
      this.clearSelectedItems();
    } else if (this.allInstaShopGalleries.length > 0) {
      this.addToInstaShopGallery(this.selectedItems);
      this.clearSelectedItems();
    }

    this.isAddButtonLoading = false;
  }

  public async onClickHideFromLike2Buy() {
    const selectedCuids = this.getSelectedCuids(this.selectedItems);
    this.openHideFromLike2BuyModal(selectedCuids);
  }

  public async onClickDeleteFromLike2Buy() {
    const selectedCuids = this.getSelectedCuids(this.selectedItems);
    this.openDeleteFromLike2BuyModal(selectedCuids);
  }

  public async onClickPinToLike2Buy() {
    const selectedCuids = this.getSelectedCuids(this.selectedItems);
    this.pinLike2BuyItems(selectedCuids);
    this.clearSelectedItems();
  }

  private getSelectedCuids(items: ISelectedItem[]): Cuid[] {
    return this.getCuidsFromPartialCuids(items.map((item) => item.cuid));
  }

  private getCuidsFromPartialCuids(pCuids: PartialCuid[]): Cuid[] {
    const parsedPartialCuids = pCuids.map((pCuid) => PartialCuid.parse(pCuid));
    return parsedPartialCuids.map((pCuid) =>
      Cuid.build({ itemType: pCuid.itemType, id: pCuid.itemId }, this.clientId),
    );
  }
  public muteMessages(title: string, type: ToastType, message: any) {
    this.$ngRedux.dispatch(
      toastNotificationsActions.addToastNotification({
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
        title: title,
        type: type,
        message: message,
      }),
    );
  }
  public async onClickMuteUnmuteSelectedItems(shouldMute: boolean) {
    let self = this;
    let cuids = this.selectedItems.map((obj: { cuid: PartialCuid }) => {
      return obj.cuid;
    });
    const videoExists = await getItemsMetadata(cuids).then((metaItem) => {
      return metaItem
        .filter((item) => isDefined(item.videoSource))
        .map((item) => {
          return Cuid.toPartial(item.rootCuid);
        });
    });
    if (videoExists.length > 0) {
      try {
        await muteSelectedItems(shouldMute, videoExists).then((res) => {
          self.muteMessages(
            'Updating selected videos...',
            ToastType.SUCCESS,
            `Updates may take a few minutes. Refresh the page to view the updated videos.`,
          );
        });
      } catch (err: any) {
        self.muteMessages(
          'error',
          ToastType.ERROR,
          getRequestErrorMessage(err),
        );
      }
    } else {
      this.$ngRedux.dispatch(
        toastNotificationsActions.addToastNotification(
          imagesSelectionFailedToast,
        ),
      );
    }
  }
}

angular.module('curalate').component('bulkActionsContainer', {
  controller: ['$ngRedux', 'BulkActionsActions', BulkActionsContainer],
  template,
});
